// Helpers for the listening dashboard stats.
//
// Two stats, two sources:
//  - Total listening time → metadata/listening_stats { sentences:{seconds}, words:{seconds} }
//  - Avg listens before answering → played_*_today (plays) / reviewed_*_today (reviews)
//
// The ratio is computed only over days that exist in the plays data. reviewed_*_today
// has full history (pre-tracking), but plays only start when tracking does, so summing
// reviews across all time would understate the ratio. Iterating the plays days and
// pulling the matching review counts keeps numerator and denominator over the same span.

export const totalListeningSeconds = (listeningStats) =>
  (listeningStats?.sentences?.seconds || 0) + (listeningStats?.words?.seconds || 0);

// seconds → "14h 23m" / "23m" / "0m". With { compact: true } the minutes are
// dropped once there's at least an hour ("14h") — used on mobile where the full
// "14h 23m" wraps onto two lines.
export const formatListeningTime = (seconds, { compact = false } = {}) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return compact ? `${h}h` : `${h}h ${m}m`;
  return `${m}m`;
};

// Avg plays per review over the days present in playedData (optionally filtered by a
// date predicate for windowing, e.g. a month). Returns null when there's no data, so
// callers can hide the stat per the "no data → don't show" rule.
export const avgListens = (playedData, reviewedData, dateFilter = null) => {
  let plays = 0;
  let reviews = 0;
  for (const [date, p] of Object.entries(playedData || {})) {
    if (dateFilter && !dateFilter(date)) continue;
    plays += p || 0;
    reviews += reviewedData?.[date] || 0;
  }
  if (reviews === 0) return null;
  return plays / reviews;
};

// A listening review always involves at least one listen, but the recorded play
// count can fall below 1/review when a play wasn't captured (e.g. autoplay was
// blocked). Never show a ratio below 1 for display. null (no data at all) stays
// null so the stat can hide entirely.
export const displayListens = (ratio) => (ratio == null ? null : Math.max(1, ratio));
